import React from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { X } from "lucide-react";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${
  (pdfjs as any).version
}/build/pdf.worker.min.mjs`;

interface FilePreviewModalsProps {
  showImagePreview: boolean;
  selectedImage: { url: string; nome: string } | null;
  showPdfPreview: boolean;
  pdfPreview: { url: string; nome: string } | null;
  pdfNumPages: number;
  pdfPageNumber: number;
  pdfRenderWidth: number;
  onCloseImage: () => void;
  onClosePdf: () => void;
  onPdfPageChange: (page: number) => void;
  onPdfLoadSuccess: (numPages: number) => void;
}

export default function FilePreviewModals({
  showImagePreview,
  selectedImage,
  showPdfPreview,
  pdfPreview,
  pdfNumPages,
  pdfPageNumber,
  pdfRenderWidth,
  onCloseImage,
  onClosePdf,
  onPdfPageChange,
  onPdfLoadSuccess,
}: FilePreviewModalsProps) {
  return (
    <>
      {/* Modal de Visualização de Imagem */}
      {showImagePreview &&
        selectedImage &&
        ReactDOM.createPortal(
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 flex items-center justify-center p-4 bg-black/50 dark:bg-black/70"
              style={{ zIndex: 999999 }}
              onClick={onCloseImage}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="relative bg-white dark:bg-gray-800 max-w-3xl h-[80vh] sm:h-auto max-h-[80vh] w-full"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Cabeçalho */}
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-between mb-2 sm:mb-4 text-gray-800 dark:text-gray-200 rounded-t-lg sm:rounded-t-xl px-3 sm:px-4 py-2 sm:py-3">
                  <h3 className="text-sm sm:text-lg font-semibold truncate pr-2">
                    {selectedImage.nome}
                  </h3>
                  <button
                    onClick={onCloseImage}
                    className="p-1.5 sm:p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex-shrink-0"
                    title="Fechar"
                  >
                    <X size={18} className="sm:w-5 sm:h-5" />
                  </button>
                </div>

                {/* Imagem */}
                <div className="bg-white dark:bg-gray-800 rounded-b-lg sm:rounded-b-xl overflow-hidden shadow-lg h-[calc(90vh-60px)] sm:h-auto">
                  <img
                    src={selectedImage.url}
                    alt={selectedImage.nome}
                    className="w-full h-full sm:h-auto max-h-full sm:max-h-[60vh] object-contain"
                  />
                </div>
              </motion.div>
            </motion.div>
          </AnimatePresence>,
          document.body
        )}

      {/* Modal de Visualização de PDF */}
      {showPdfPreview &&
        pdfPreview &&
        ReactDOM.createPortal(
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 flex items-center justify-center p-2 sm:p-4 bg-black/50 dark:bg-black/70"
              style={{ zIndex: 999999 }}
              onClick={onClosePdf}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="relative bg-white dark:bg-gray-800 max-w-5xl max-h-[95vh] sm:max-h-[85vh] w-full h-[95vh] sm:h-auto rounded-lg sm:rounded-xl mx-1"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Cabeçalho */}
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mb-2 text-gray-800 dark:text-gray-200 rounded-t-lg sm:rounded-t-xl px-3 sm:px-4 py-2 sm:py-3">
                  <h3 className="text-sm sm:text-lg font-semibold truncate pr-2">
                    {pdfPreview.nome}
                  </h3>
                  <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                    <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                      {pdfNumPages > 0 && (
                        <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                          Página {pdfPageNumber} de {pdfNumPages}
                        </span>
                      )}
                      <button
                        onClick={() => onPdfPageChange(Math.max(1, pdfPageNumber - 1))}
                        disabled={pdfPageNumber <= 1}
                        className="px-1.5 sm:px-2 py-1 text-xs sm:text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded disabled:opacity-50"
                      >
                        Anterior
                      </button>
                      <button
                        onClick={() =>
                          onPdfPageChange(
                            Math.min(pdfNumPages || pdfPageNumber + 1, pdfPageNumber + 1)
                          )
                        }
                        disabled={pdfNumPages > 0 ? pdfPageNumber >= pdfNumPages : false}
                        className="px-1.5 sm:px-2 py-1 text-xs sm:text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded disabled:opacity-50"
                      >
                        Próxima
                      </button>
                      <a
                        href={pdfPreview.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm bg-blue-600 dark:bg-primary-500 text-white rounded hover:bg-blue-700 dark:hover:bg-primary-600 whitespace-nowrap"
                      >
                        Nova aba
                      </a>
                    </div>
                    <button
                      onClick={onClosePdf}
                      className="p-1.5 sm:p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex-shrink-0"
                      title="Fechar"
                    >
                      <X size={18} className="sm:w-5 sm:h-5" />
                    </button>
                  </div>
                </div>
                {/* PDF */}
                <div className="bg-white dark:bg-gray-800 rounded-b-lg sm:rounded-b-xl overflow-hidden shadow-lg">
                  <div className="w-full h-[calc(95vh-80px)] sm:h-[75vh] overflow-auto flex justify-center">
                    <Document
                      file={pdfPreview.url}
                      onLoadSuccess={({ numPages }) => onPdfLoadSuccess(numPages)}
                      onLoadError={(err) =>
                        console.error("Erro carregando PDF:", err)
                      }
                    >
                      <Page pageNumber={pdfPageNumber} width={pdfRenderWidth} />
                    </Document>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </AnimatePresence>,
          document.body
        )}
    </>
  );
}

